/** Identifiants durables repris dans le registre documentaire du 21 octobre.
 * Un sprint désigne le travail responsable, pas une promesse que l'ajout du
 * libellé rend le calcul disponible. Les domaines Projet/Temps gardent leurs
 * dépendances canoniques S7/S10 ; les calculs globaux restent à terminer en S5.
 */
export const financialCalculationRoadmap = {
  sources: {
    id: 'S5-CALC-01',
    sprint: 5,
    lot: 'S5-02',
    reason: 'Qualifier les montants, dates et la couverture des sources Freebe.',
  },
  cashHistory: {
    id: 'S5-CALC-02',
    sprint: 5,
    lot: 'S5-02',
    reason: 'Vérifier compte, ancrage daté, mouvements et périodes de l’historique bancaire.',
  },
  provisions: {
    id: 'S5-CALC-03',
    sprint: 5,
    lot: 'S5-03',
    reason:
      'Raccorder le registre exhaustif des obligations restant à payer, sans retraitement des paiements déjà réglés.',
  },
  urssaf: {
    id: 'S5-CALC-04',
    sprint: 5,
    lot: 'S5-03',
    reason:
      'Relier le PDF officiel, sa période et les règlements indiqués dans Freebe ou explicitement attribués.',
  },
  socialEstimate: {
    id: 'S5-CALC-05',
    sprint: 5,
    lot: 'S5-03',
    reason:
      'Établir l’estimation sur les recettes HT encaissées avec les taux d’un PDF daté et distinguer les paiements.',
  },
  vat: {
    id: 'S5-CALC-06',
    sprint: 5,
    lot: 'S5-03',
    reason:
      'Séparer TVA collectée, déductible, crédits, déclaration trimestrielle, paiements et reste dû.',
  },
  scheduledExpenses: {
    id: 'S5-CALC-07',
    sprint: 5,
    lot: 'S5-05',
    reason:
      'Établir les échéances et dépenses encore attendues ; les dépenses déjà payées ne sont pas une nouvelle dette.',
  },
  remunerationAdvice: {
    id: 'S5-CALC-08',
    sprint: 5,
    lot: 'S5-03',
    reason:
      'Raccorder les obligations restantes au conseil de rémunération, avec réserve et rémunération déjà prise.',
  },
  retainedCash: {
    id: 'S5-CALC-09',
    sprint: 5,
    lot: 'S5-03',
    reason:
      'Calculer le flux conservé après le supplément conseillé sans retirer la rémunération une seconde fois.',
  },
  breakEven: {
    id: 'S5-CALC-10',
    sprint: 5,
    lot: 'S5-05',
    reason:
      'Qualifier charges fixes/variables, capital d’emprunt et taux datés avant de conseiller un point zéro.',
  },
  expenseRatio: {
    id: 'S5-CALC-11',
    sprint: 5,
    lot: 'S5-05',
    reason:
      'Contrôler les bases positives et homogènes : HT / CA HT dans le tableau, paiement net / total net payé dans le diagramme.',
  },
  clientCosts: {
    id: 'S5-CALC-12',
    sprint: 5,
    lot: 'S5-05',
    reason:
      'Définir et raccorder l’imputation minimale des coûts au Client ; enrichissement Projet en S7 et temps en S10.',
  },
  clientMargin: {
    id: 'S5-CALC-13',
    sprint: 5,
    lot: 'S5-05',
    reason:
      'Calculer une marge Client seulement avec recettes et coûts rattachés complets ; distinguer les futurs enrichissements S7/S10.',
  },
  capitalGoal: {
    id: 'S5-CALC-14',
    sprint: 5,
    lot: 'S5-03',
    reason:
      'Contrôler le cumul annuel des flux et sa progression vers l’objectif réglé ; une période absente interrompt le cumul.',
  },
  settings: {
    id: 'S5-CALC-15',
    sprint: 5,
    lot: 'S5-03',
    reason:
      'Renseigner le paramètre dans les réglages déjà raccordés ; aucun objectif par défaut n’est supposé.',
  },
  expenseHistory: {
    id: 'S5-CALC-16',
    sprint: 5,
    lot: 'S5-03',
    reason:
      'Contrôler les mois clos, les dépenses et les recettes sur les mêmes périodes avant moyenne et ratio.',
  },
  projectMargin: {
    id: 'S7-CALC-01',
    sprint: 7,
    lot: 'S7-05',
    reason:
      'Raccorder les Projets, devis, factures et coûts confirmés ; les coûts de temps réel seront enrichis en S10-03.',
  },
  timeCosts: {
    id: 'S10-CALC-01',
    sprint: 10,
    lot: 'S10-03',
    reason: 'Raccorder les temps réellement imputés et leurs taux validés aux coûts et rapports.',
  },
} as const;

export type FinancialCalculationKey = keyof typeof financialCalculationRoadmap;
export function calculationFollowupText(key: FinancialCalculationKey): string {
  const sprint = financialCalculationRoadmap[key].sprint;
  if (key === 'settings') return `Réglage à renseigner : S${sprint}`;
  if (['sources', 'cashHistory', 'capitalGoal', 'expenseHistory', 'expenseRatio'].includes(key))
    return `Sources / calcul à contrôler : S${sprint}`;
  return sprint === 5 ? `Données à compléter : S${sprint}` : `Raccordement prévu : S${sprint}`;
}
export function unavailableCalculationText(
  key: FinancialCalculationKey,
  label = 'Non calculable',
): string {
  return `${label} · raccordement S${financialCalculationRoadmap[key].sprint}`;
}
